/**
 * Scene 3 — Active-Threat scenario script.
 *
 * Deterministic, time-sequenced events. The component "plays" the timeline
 * with setTimeout — no real audio classification runs in the browser.
 * The numbers (confidences, scores) come from realistic ranges that the
 * production stack (YAMNet + openSMILE) would produce for this scenario.
 */

export interface ScenarioEvent {
  /** Milliseconds from scenario start. */
  t: number;
  /** Event id — drives which card lights up in the UI. */
  kind:
    | "ambient-shift"
    | "acoustic-detect"
    | "stress-spike"
    | "triangulation"
    | "hot-mic"
    | "silent-route"
    | "runbook"
    | "approval-request";
  /** Free-form data per event, picked up by the React component. */
  payload: Record<string, unknown>;
}

export const SCENARIO_TOTAL_MS = 5700;

export const SCENARIO_EVENTS: ReadonlyArray<ScenarioEvent> = [
  {
    t: 0,
    kind: "ambient-shift",
    payload: {
      source: "Site acoustic sensors · ceiling-mic array (mics 3, 4, 7, 9)",
      note: "Background acoustic floor changes on the dedicated hall mics — sustained machinery hum drops, voices rise.",
    },
  },
  {
    t: 420,
    kind: "acoustic-detect",
    payload: {
      class: "firearm",
      confidence: 0.94,
      modelLabel: "YAMNet · 521 classes",
      shotCount: 1,
    },
  },
  {
    t: 950,
    kind: "stress-spike",
    payload: {
      talkgroup: "TG-7 Inbound North",
      baselineScore: 0.18,
      currentScore: 0.81,
      speakers: 6,
    },
  },
  {
    t: 1480,
    kind: "triangulation",
    payload: {
      zone: "Inbound · North aisle",
      offsetMeters: 12,
      micsContributing: 4,
    },
  },
  {
    t: 1900,
    kind: "hot-mic",
    payload: {
      radios: 4,
      durationSeconds: 30,
      ledState: "visible (TX-LED on)",
      auditEvent: "incident::hot_mic.activated",
      reasoning: "Closest 4 radios in the affected zone enabled for acoustic verification. Auto-stop after 30s, never covert, never preventive.",
    },
  },
  {
    t: 2700,
    kind: "silent-route",
    payload: {
      audience: "Security shift lead · Talkgroup S-9",
      mode: "silent",
      reasoning: "Public talkgroups suppressed — broadcast would compromise containment.",
    },
  },
  {
    t: 3450,
    kind: "runbook",
    payload: {
      title: "Silent Runbook (suggested)",
      steps: [
        "Pre-position response team at Exits A2 and C1.",
        "Lock down Inbound · North aisle access doors.",
        "Hold public-address until shift-lead approval.",
        "Notify on-site EMT — staging area break room B3.",
      ],
    },
  },
  {
    t: 4600,
    kind: "approval-request",
    payload: {
      handle: "Shift lead · Maya",
      note: "Approval required before evacuation order goes to public talkgroups.",
    },
  },
];
